import React from "react";

// Rosterly-brand posters — clean, typographic IMAGES. No icons, no buttons,
// no logo marks (just the "Rosterly" wordmark). Palettes: black+purple,
// black+white, white+purple.
const PURP = "#9D4CDD";
const PURP_SOFT = "#C9A7F0";

const Shell = ({ children, style, testId, light }) => (
    <article data-testid={testId} data-orientation="h" className={`relative overflow-hidden rounded-2xl border ${light ? "border-black/10" : "border-white/10"} aspect-[16/9] shadow-lg shadow-black/50`} style={style}>{children}</article>
);
const Word = ({ color }) => <span className="absolute top-4 left-6 z-20 font-heading font-black uppercase tracking-tight text-[13px]" style={{ color }}>Rosterly</span>;

// 1 — FEATURE (black + purple)
function Feature({ s, testId }) {
    return (
        <Shell testId={testId} style={{ background: `radial-gradient(110% 130% at 10% 110%, ${PURP}55 0%, transparent 55%), #08070C` }}>
            <Word color="rgba(255,255,255,0.55)" />
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <div className="flex items-center gap-2 mb-1"><span className="h-2.5 w-8 rounded-full" style={{ background: PURP }} /><span className="text-[10px] font-black uppercase tracking-[0.35em]" style={{ color: PURP_SOFT }}>{s.kicker || "New Feature"}</span></div>
                <h2 className="font-story-punch uppercase text-white text-7xl leading-[0.8]">{s.feature || "Stories"}</h2>
                <p className="text-white/55 text-sm mt-3 max-w-md">{s.subtitle || "Turn any moment into a share-ready poster."}</p>
            </div>
        </Shell>
    );
}

// 2 — HIRING (black + white)
function Hiring({ s, testId }) {
    return (
        <Shell testId={testId} style={{ background: "#0A0A0C" }}>
            <Word color="rgba(255,255,255,0.55)" />
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <span className="text-[10px] font-black uppercase tracking-[0.4em] text-white/40">{s.kicker || "Careers"}</span>
                <h2 className="font-story-punch uppercase text-white text-7xl leading-[0.78] mt-1">We're<br />Hiring</h2>
                <div className="mt-4 h-px w-16 bg-white/40" />
                <div className="mt-3 font-heading uppercase font-black text-white text-lg tracking-wide">{s.role || "Senior Product Designer"}</div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/45 mt-1">{s.meta || "Remote · Full-time"}</div>
            </div>
        </Shell>
    );
}

// 3 — PRICING (white + purple)
function Pricing({ s, testId }) {
    const plans = s.plans || [
        { name: "Free", price: "$0", feats: ["1 team", "Basic posters"] },
        { name: "Pro", price: "$12", feats: ["Unlimited", "All templates", "HD exports"], hot: true },
        { name: "Team", price: "$29", feats: ["5 seats", "Brand kit", "Priority"] },
    ];
    return (
        <Shell testId={testId} light style={{ background: "#F5F2EA" }}>
            <Word color="#111" />
            <span className="absolute top-4 right-6 z-20 text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: PURP }}>Simple Pricing</span>
            <div className="relative z-10 h-full flex items-stretch px-6 pt-11 pb-5">
                {plans.map((p, i) => (
                    <div key={i} className={`flex-1 flex flex-col ${i > 0 ? "border-l border-black/15 pl-4" : ""} ${i < plans.length - 1 ? "pr-4" : ""}`}>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: p.hot ? PURP : "#111" }}>{p.name}{p.hot && " ★"}</span>
                        <div className="font-story-punch text-black text-4xl leading-none mt-1">{p.price}<span className="text-[11px] text-black/40 font-heading">/mo</span></div>
                        <div className="mt-3 space-y-1.5">{p.feats.map((f, j) => <div key={j} className="flex items-center gap-2 text-[10px] text-black/60"><span className="h-1 w-1 rounded-full" style={{ background: p.hot ? PURP : "#111" }} />{f}</div>)}</div>
                    </div>
                ))}
            </div>
        </Shell>
    );
}

// 4 — CHANGELOG (black + purple)
function Changelog({ s, testId }) {
    const items = s.items || ["New Stories timeline", "20+ poster templates", "2× faster exports"];
    return (
        <Shell testId={testId} style={{ background: `radial-gradient(120% 120% at 100% 0%, ${PURP}33 0%, transparent 50%), #08070C` }}>
            <Word color="rgba(255,255,255,0.55)" />
            <span className="absolute top-4 right-6 z-20 font-story-punch text-lg" style={{ color: PURP_SOFT }}>{s.version || "v2.4"}</span>
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <span className="text-[10px] font-black uppercase tracking-[0.35em] text-white/45">{s.kicker || "Product Update"}</span>
                <h2 className="font-story-punch uppercase text-white text-5xl leading-none mt-1 mb-4">What's New</h2>
                <div className="space-y-2.5">{items.map((it, i) => <div key={i} className="flex items-baseline gap-3"><span className="font-story-punch text-sm tabular-nums" style={{ color: PURP }}>{String(i + 1).padStart(2, "0")}</span><span className="text-white/80 text-sm">{it}</span></div>)}</div>
            </div>
        </Shell>
    );
}

// 5 — WELCOME (white + purple)
function Welcome({ s, testId }) {
    const steps = s.steps || ["Create your team", "Design a poster", "Share anywhere"];
    return (
        <Shell testId={testId} light style={{ background: `radial-gradient(120% 120% at 100% 100%, ${PURP}22 0%, transparent 55%), #F5F2EA` }}>
            <Word color="#111" />
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <span className="text-[10px] font-black uppercase tracking-[0.35em]" style={{ color: PURP }}>Get Started</span>
                <h2 className="font-story-punch uppercase text-black text-6xl leading-[0.8] mt-1">Welcome to<br />Rosterly</h2>
                <div className="mt-4 flex items-center gap-5 flex-wrap">{steps.map((st, i) => (<div key={i} className="flex items-baseline gap-2"><span className="font-story-punch text-xl" style={{ color: PURP }}>{String(i + 1).padStart(2, "0")}</span><span className="text-[12px] uppercase tracking-wider font-bold text-black/70">{st}</span></div>))}</div>
            </div>
        </Shell>
    );
}

// 6 — METRIC (black + purple)
function Metric({ s, testId }) {
    return (
        <Shell testId={testId} style={{ background: `radial-gradient(90% 120% at 50% 120%, ${PURP}55 0%, transparent 60%), #08070C` }}>
            <Word color="rgba(255,255,255,0.55)" />
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center">
                <span className="text-[10px] font-black uppercase tracking-[0.4em]" style={{ color: PURP_SOFT }}>Milestone</span>
                <div className="font-story-punch text-white text-8xl leading-none mt-1" style={{ textShadow: `0 0 40px ${PURP}77` }}>{s.number || "10,000"}</div>
                <div className="text-white/55 text-sm uppercase tracking-[0.3em] mt-3">{s.label || "Creators on Rosterly"}</div>
            </div>
        </Shell>
    );
}

// 7 — EVENT (black + white)
function Event({ s, testId }) {
    return (
        <Shell testId={testId} style={{ background: "#0A0A0C" }}>
            <Word color="rgba(255,255,255,0.55)" />
            <span className="absolute top-4 right-6 z-20 text-[10px] font-black uppercase tracking-[0.3em] text-white/40">Rosterly Live</span>
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <div className="h-px w-16 bg-white/40 mb-4" />
                <h2 className="font-story-punch uppercase text-white text-7xl leading-[0.8]">{s.title || "Demo Day"}</h2>
                <div className="text-white/60 text-lg font-heading uppercase tracking-wide mt-3">{s.when || "Thu, Jun 26 · 2PM ET"}</div>
            </div>
        </Shell>
    );
}

// 8 — QUOTE (white + purple)
function QuoteCard({ s, testId }) {
    return (
        <Shell testId={testId} light style={{ background: "#F5F2EA" }}>
            <Word color="#111" />
            <div className="relative z-10 h-full flex flex-col justify-center px-7">
                <span className="font-story-serif leading-none mb-1" style={{ color: PURP, fontSize: "4.5rem" }}>&ldquo;</span>
                <p className="font-story-serif text-black text-2xl leading-tight max-w-lg -mt-6">{s.quote || "Rosterly turned our game recaps into graphics fans actually share."}</p>
                <div className="mt-4"><div className="font-heading uppercase font-black text-black text-xs tracking-wide">{s.author || "Alex Rivera"}</div><div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: PURP }}>{s.role || "Content Lead"}</div></div>
            </div>
        </Shell>
    );
}

// 9 — REFERRAL (black + purple)
function Referral({ s, testId }) {
    return (
        <Shell testId={testId} style={{ background: `radial-gradient(120% 120% at 90% 15%, ${PURP}44 0%, transparent 55%), #08070C` }}>
            <Word color="rgba(255,255,255,0.55)" />
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <span className="text-[10px] font-black uppercase tracking-[0.35em]" style={{ color: PURP_SOFT }}>Refer a Friend</span>
                <h2 className="font-story-punch uppercase text-white text-6xl leading-[0.8] mt-1">{s.headline || "Give $20,\nGet $20"}</h2>
                <div className="mt-4 inline-flex self-start items-center rounded-lg px-4 py-2" style={{ border: `1.5px solid ${PURP}` }}><span className="font-mono text-white font-bold tracking-[0.3em]">{s.code || "ROSTER20"}</span></div>
            </div>
        </Shell>
    );
}

// 10 — SALE (purple + white)
function Sale({ s, testId }) {
    return (
        <Shell testId={testId} style={{ background: `linear-gradient(125deg, ${PURP} 0%, #5B21B6 55%, #2E1065 120%)` }}>
            <Word color="rgba(255,255,255,0.85)" />
            <div className="relative z-10 h-full flex flex-col justify-center px-6">
                <span className="text-[11px] font-black uppercase tracking-[0.4em] text-white/75">Limited Time</span>
                <h2 className="font-story-punch uppercase text-white leading-[0.76] text-8xl mt-1">{s.big || "50% Off"}</h2>
                <div className="text-white/85 text-base uppercase tracking-[0.2em] mt-3 font-heading">{s.sub || "Rosterly Pro"}</div>
                <div className="text-white/55 text-[11px] uppercase tracking-[0.25em] mt-1">{s.note || "Ends Sunday"}</div>
            </div>
        </Shell>
    );
}

export const BRAND_STYLES = { feature: Feature, hiring: Hiring, pricing: Pricing, changelog: Changelog, welcome: Welcome, metric: Metric, event: Event, quote: QuoteCard, referral: Referral, sale: Sale };

export function BrandPoster({ s, testId }) {
    const C = BRAND_STYLES[s.style] || Feature;
    return <C s={s} testId={testId} />;
}
